import { useId, useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import {
  formatReportCell,
  type ReportFieldType,
  type ReportFormat,
  type WorkspaceFormats,
} from '@eis/core/reporting';

const KINDS: readonly ReportFormat['kind'][] = [
  'DATE',
  'DATETIME',
  'NUMBER',
  'CURRENCY',
  'PERCENT',
  'BOOLEAN',
  'TEXT',
];
const DATE_PATTERNS = ['DD.MM.YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY'];
const DATETIME_PATTERNS = ['DD.MM.YYYY HH:mm', 'YYYY-MM-DD HH:mm', 'DD.MM.YYYY HH:mm:ss'];

export interface FormatDialogProps {
  readonly open: boolean;
  /** The field or value as the editor shows it: "Prämie Gesamtsumme". */
  readonly fieldLabel: string;
  readonly fieldType: ReportFieldType;
  /** The current override (undefined: the workspace default applies) and the title override. */
  readonly format?: ReportFormat;
  readonly title?: string;
  readonly defaults: WorkspaceFormats;
  /** STRING fields of the dataset a currency may be read from per row (`prämie währung`). */
  readonly currencyFields: readonly string[];
  /** `format` undefined = back to the workspace default. */
  readonly onApply: (format: ReportFormat | undefined, title: string | undefined) => void;
  readonly onClose: () => void;
}

function defaultKind(type: ReportFieldType): ReportFormat['kind'] {
  return type === 'STRING' ? 'TEXT' : type;
}

/**
 * The per-field format (SDD-024 F-2, D-4; mockup board 6): the title in the report, the kind, and
 * what the kind needs — decimals, a currency fixed or read from a row's field, a date pattern, the
 * yes/no labels, the empty mark. A live preview renders a sample through the same formatter the
 * viewer uses; "Auf Standard zurücksetzen" removes the override.
 */
export function FormatDialog(props: FormatDialogProps): ReactNode {
  // The form mounts with the dialog: a reopened dialog starts from the current override.
  return props.open ? <FormatDialogOpen {...props} /> : null;
}

function FormatDialogOpen(props: FormatDialogProps): ReactNode {
  const { t, i18n } = useTranslation('patterns');
  const titleId = useId();
  const { defaults } = props;
  const [title, setTitle] = useState(props.title ?? '');
  const [format, setFormat] = useState<ReportFormat>(
    props.format ?? { kind: defaultKind(props.fieldType) },
  );

  const set = (patch: Partial<ReportFormat>): void => {
    setFormat((prev) => ({ ...prev, ...patch }));
  };
  const sample =
    format.kind === 'DATE' || format.kind === 'DATETIME'
      ? '2026-10-31 01:10:00'
      : format.kind === 'BOOLEAN'
        ? 'true'
        : format.kind === 'PERCENT'
          ? '0.1234'
          : format.kind === 'TEXT'
            ? props.fieldLabel
            : '1234.56';
  const locale = i18n.language.startsWith('en') ? 'en-GB' : 'de-DE';
  const preview = formatReportCell(sample, {
    type: format.kind === 'BOOLEAN' ? 'BOOLEAN' : props.fieldType,
    format,
    settings: defaults,
    locale,
  });
  const summary = `${defaults.formats.currency.code}, ${String(defaults.formats.number.decimals)} · ${defaults.formats.date.pattern} · ${defaults.locale ?? 'de-DE'}`;
  const numeric =
    format.kind === 'NUMBER' || format.kind === 'CURRENCY' || format.kind === 'PERCENT';

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      aria-labelledby={titleId}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id={titleId}>
        {t('reporting.format.title', { field: props.fieldLabel })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            size="small"
            label={t('reporting.format.label')}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
          <TextField
            select
            size="small"
            label={t('reporting.format.kind')}
            value={format.kind}
            onChange={(event) => {
              setFormat({ kind: event.target.value as ReportFormat['kind'] });
            }}
          >
            {KINDS.map((kind) => (
              <MenuItem key={kind} value={kind}>
                {t(`reporting.format.kinds.${kind}`)}
              </MenuItem>
            ))}
          </TextField>
          {numeric && (
            <TextField
              size="small"
              type="number"
              label={t('reporting.format.decimals')}
              value={format.decimals ?? ''}
              slotProps={{ htmlInput: { min: 0, max: 6 } }}
              onChange={(event) => {
                set({
                  decimals: event.target.value === '' ? undefined : Number(event.target.value),
                });
              }}
            />
          )}
          {format.kind === 'NUMBER' && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={format.grouping ?? defaults.formats.number.grouping}
                  onChange={(event) => {
                    set({ grouping: event.target.checked });
                  }}
                />
              }
              label={t('reporting.format.grouping')}
            />
          )}
          {format.kind === 'CURRENCY' && (
            <TextField
              select
              size="small"
              label={t('reporting.format.currencyField')}
              value={format.currencyField ?? ''}
              onChange={(event) => {
                const value = event.target.value;
                setFormat((prev) => {
                  const { currencyField: _drop, ...rest } = prev;
                  return value === '' ? rest : { ...rest, currencyField: value };
                });
              }}
            >
              <MenuItem value="">{t('reporting.format.currencyFixed')}</MenuItem>
              {props.currencyFields.map((field) => (
                <MenuItem key={field} value={field}>
                  {field}
                </MenuItem>
              ))}
            </TextField>
          )}
          {format.kind === 'CURRENCY' && format.currencyField === undefined && (
            <TextField
              size="small"
              label={t('reporting.format.currency')}
              value={format.currency ?? defaults.formats.currency.code}
              slotProps={{ htmlInput: { maxLength: 3 } }}
              onChange={(event) => {
                set({ currency: event.target.value.toUpperCase() });
              }}
            />
          )}
          {(format.kind === 'DATE' || format.kind === 'DATETIME') && (
            <TextField
              select
              size="small"
              label={t('reporting.format.pattern')}
              value={
                format.pattern ??
                (format.kind === 'DATE'
                  ? defaults.formats.date.pattern
                  : defaults.formats.datetime.pattern)
              }
              onChange={(event) => {
                set({ pattern: event.target.value });
              }}
            >
              {(format.kind === 'DATE' ? DATE_PATTERNS : DATETIME_PATTERNS).map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          )}
          {format.kind === 'BOOLEAN' && (
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                label={t('reporting.format.trueLabel')}
                value={format.trueLabel ?? defaults.formats.boolean.trueLabel}
                onChange={(event) => {
                  set({ trueLabel: event.target.value });
                }}
              />
              <TextField
                size="small"
                label={t('reporting.format.falseLabel')}
                value={format.falseLabel ?? defaults.formats.boolean.falseLabel}
                onChange={(event) => {
                  set({ falseLabel: event.target.value });
                }}
              />
            </Stack>
          )}
          <TextField
            size="small"
            label={t('reporting.format.empty')}
            value={format.empty ?? defaults.formats.empty}
            slotProps={{ htmlInput: { maxLength: 10 } }}
            onChange={(event) => {
              set({ empty: event.target.value });
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {t('reporting.format.defaults', { summary })}
          </Typography>
          <Typography variant="body2" data-testid="format-preview">
            {t('reporting.format.preview', { value: preview })}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          onClick={() => {
            props.onApply(undefined, title === '' ? undefined : title);
          }}
        >
          {t('reporting.format.reset')}
        </Button>
        <Button size="small" onClick={props.onClose}>
          {t('reporting.format.cancel')}
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            props.onApply(format, title === '' ? undefined : title);
          }}
        >
          {t('reporting.format.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
